import { styled, Theme } from '@mui/material';
import { ReactNode } from 'react';
import { NavLink } from 'react-router';

import { I18nResources, useI18n } from '@app-velocity/i18n';

import { Badge } from '../../../Badge';

const StyledNav = styled('nav')(({ theme }) => ({}));

const StyledList = styled('ul')(({ theme }) => ({
  listStyleType: 'none',
  margin: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  padding: `${theme.spacing(4)} ${theme.spacing(1)}`,
}));

type NavigationItemStyleProps = {
  disabled?: boolean;
};

const navigationItemStyles = ({
  theme,
  disabled,
}: NavigationItemStyleProps & { theme: Theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  alignItems: 'center',
  fontWeight: 500,
  fontSize: 16,
  opacity: disabled ? 0.3 : 1,
  borderRadius: theme.spacing(6),
  padding: `${theme.spacing(3)} ${theme.spacing(4)}`,
  color: 'var(--text-text-secondary-color)',
  svg: {
    fill: 'var(--icon-icon-secondary-color)',
  },
});

const navigationItemHoverStyles = {
  color: 'var(--icon-icon-secondary-color)',
  svg: {
    fill: 'var(--icon-icon-secondary-color)',
  },
};

const navigationItemActiveStyles = {
  background: '#efefef',
  color: '#1b1b1b',
  svg: {
    fill: '#1b1b1b',
  },
};

const StyledNavLink = styled(NavLink)<NavigationItemStyleProps>(
  navigationItemStyles,
  {
    '&:link,&:visited,&:hover': navigationItemHoverStyles,
    '&:active, &.active': navigationItemActiveStyles,
  }
);

const StyledNavButton = styled('button')<NavigationItemStyleProps>(
  {
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    font: 'inherit',
    textAlign: 'left',
    width: '100%',
  },
  navigationItemStyles,
  {
    '&:hover': navigationItemHoverStyles,
    '&:active, &.active': navigationItemActiveStyles,
  }
);

export type NavigationItem = {
  label: I18nResources;
  path?: string;
  onClick?: () => void;
  icon?: ReactNode;
  beta?: boolean;
};

export type NavigationProps = {
  items: NavigationItem[];
};

export const Navigation = ({ items }: NavigationProps) => {
  const { t } = useI18n();

  return (
    <StyledNav>
      <StyledList>
        {items.map((item, index) => (
          <li key={item.label.toString() || index}>
            {item.path ? (
              <StyledNavLink end to={item.path}>
                {item.icon}
                {t(item.label)}
                {item.beta ? <Badge>Beta</Badge> : null}
              </StyledNavLink>
            ) : (
              <StyledNavButton type="button" onClick={item.onClick}>
                {item.icon}
                {t(item.label)}
                {item.beta ? <Badge>Beta</Badge> : null}
              </StyledNavButton>
            )}
          </li>
        ))}
      </StyledList>
    </StyledNav>
  );
};
